import { SphereButton } from "@components/common/sphere-button";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { BaseProjectProps } from "@custom-types/project-types";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import {
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Skeleton,
} from "@mui/material";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { blue, neutral, ArrowDownIcon } from "@flat-ui";
import {
  DEFAULT_BOLD_TEXT_FONT_WEIGHT,
  DEFAULT_INPUT_FONT_SIZE,
  getSvgColoredIconCss,
  withEllipsis,
} from "@styles/common-styles";
import {
  OPEN_PROJECT_TARGET_ATTRIBUTE,
  OpenProjectProps,
  getOpenProjectHref,
  getOpenProjectTargetName,
  trackOpenProject,
} from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { clickStyle, hoverStyle } from "@utils/ui-utils";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface Props extends BaseProjectProps {
  /** Flag to show a loading state */
  isLoading: boolean;
}

/**
 * Button to open a project in the Sphere Viewer or WebEditor
 */
export function OpenProjectButton({ project, isLoading }: Props): JSX.Element {
  const { trackAsyncEvent } = useTrackEvent();
  const projectLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const shouldShowOpenInHBButton =
    projectLaunchSettings.shouldShowOpenInWebEditorBtn;

  /**
   * Defines the target that this project should be opened in.
   */
  const openTargetProps = useMemo(() => {
    // The default target.
    const target = projectLaunchSettings.defaultOpenProjectTarget;
    const targetName = getOpenProjectTargetName(target);
    const targetHref = getOpenProjectHref({
      openTarget: target,
      projectId: project.id,
    });
    // The target that is not the default one.
    const otherTarget =
      projectLaunchSettings.defaultOpenProjectTarget ===
      ProjectLaunchTarget.sphereViewer
        ? ProjectLaunchTarget.webEditor
        : ProjectLaunchTarget.sphereViewer;
    const otherTargetName = getOpenProjectTargetName(otherTarget);
    const otherTargetHref = getOpenProjectHref({
      openTarget: otherTarget,
      projectId: project.id,
    });

    return {
      target,
      targetName,
      targetHref,
      otherTarget,
      otherTargetName,
      otherTargetHref,
    };
  }, [projectLaunchSettings.defaultOpenProjectTarget, project.id]);

  /** Returns the button to open the project in the default platform */
  const openProjectButton = useMemo(() => {
    // If shouldShowDefaultAppSetting is true, there is more than one option to open the project.
    const openText = projectLaunchSettings.shouldShowDefaultAppSetting
      ? `Open in ${openTargetProps.targetName}`
      : "Open project";

    const sx = {
      fontSize: DEFAULT_INPUT_FONT_SIZE,
      fontWeight: DEFAULT_BOLD_TEXT_FONT_WEIGHT,
      textTransform: "none",
      display: "inline-block",
      alignItems: "center",
      gap: "12px",
      width: {
        xs: shouldShowOpenInHBButton ? "90px" : "121px",
        sm: "auto",
      },
      ml: "10px",
      ...withEllipsis,
    };

    return (
      <SphereButton
        sx={sx}
        to={openTargetProps.targetHref}
        target={OPEN_PROJECT_TARGET_ATTRIBUTE}
        onClick={() => {
          void triggerTrackOpenProject({
            clickType: "left button click",
            openTarget: openTargetProps.target,
          });
        }}
        onAuxClick={() => {
          void triggerTrackOpenProject({
            clickType: "middle button click",
            openTarget: openTargetProps.target,
          });
        }}
        onContextMenu={() => {
          void triggerTrackOpenProject({
            clickType: "open context menu",
            openTarget: openTargetProps.target,
          });
        }}
      >
        {openText}
      </SphereButton>
    );
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't need triggerTrackOpenProject as a dependency
  }, [openTargetProps, projectLaunchSettings.shouldShowDefaultAppSetting]);

  function handleClose(event: Event): void {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setIsOpen(false);
  }

  /**
   * Tracks whenever a user opens a project, including the number of members and the target
   */
  async function triggerTrackOpenProject({
    clickType,
    openTarget,
  }: Pick<OpenProjectProps, "clickType" | "openTarget">): Promise<void> {
    await trackOpenProject({
      openFrom: "projectDetails",
      numberOfMembers: project.members.length,
      openTarget,
      clickType,
      ...project,
      trackAsyncEvent,
    });
  }

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        width="157px"
        height="36px"
        sx={{ ml: "10px" }}
      />
    );
  }

  if (!projectLaunchSettings.shouldShowDefaultAppSetting) {
    // If shouldShowDefaultAppSetting is false, there is only one option to open the project.
    // So we don't need to show the dropdown button.
    return openProjectButton;
  }

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        sx={{
          "&.MuiButtonGroup-root .MuiButtonGroup-firstButton": {
            borderRightColor: blue[600],
          },
        }}
      >
        {openProjectButton}
        {shouldShowOpenInHBButton && (
          <SphereIconButton
            component={ArrowDownIcon}
            iconSize="18px"
            buttonSize="36px"
            color={neutral[0]}
            onClick={() => setIsOpen((wasPrevOpen) => !wasPrevOpen)}
            iconButtonProps={{
              sx: {
                backgroundColor: isOpen ? blue[700] : blue[500],
                border: `1px solid ${blue[600]}`,
                // Add a negative left margin to avoid having the border of both buttons form a border of double width
                marginLeft: "-1px",
                borderRadius: "0px 4px 4px 0px",
                "&:hover": {
                  ...hoverStyle,
                  backgroundColor: blue[600],
                  ...getSvgColoredIconCss(neutral[0]),
                },
                "&:active": {
                  backgroundColor: blue[700],
                },
                "& svg": {
                  transform: isOpen ? "rotate(-180deg)" : "",
                  transitionDuration: "0.3s",
                  transitionProperty: "transform",
                },
              },
            }}
          />
        )}
      </ButtonGroup>
      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
      >
        {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper sx={{ padding: "4px", marginTop: "4px" }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem sx={{ padding: "4px" }}>
                  {shouldShowOpenInHBButton && (
                    <Link
                      target={OPEN_PROJECT_TARGET_ATTRIBUTE}
                      to={openTargetProps.otherTargetHref}
                      style={{
                        color: neutral[800],
                        textDecoration: "none",
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          void triggerTrackOpenProject({
                            clickType: "left button click",
                            openTarget: openTargetProps.otherTarget,
                          });
                        }}
                        onAuxClick={() => {
                          void triggerTrackOpenProject({
                            clickType: "middle button click",
                            openTarget: openTargetProps.otherTarget,
                          });
                        }}
                        onContextMenu={() => {
                          void triggerTrackOpenProject({
                            clickType: "open context menu",
                            openTarget: openTargetProps.otherTarget,
                          });
                        }}
                        disableRipple
                        sx={{
                          padding: "8px",
                          fontSize: "12px",
                          "&:hover": {
                            color: blue[500],
                          },
                          "&:active": {
                            ...clickStyle,
                          },
                        }}
                      >
                        Open in {openTargetProps.otherTargetName}
                      </MenuItem>
                    </Link>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
